import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import { TPOGEN_OPTIMIZED_PROMOTED_TO_ORIGINAL } from 'utils/mixpanelEventNames';
import { useEditWriteGuard } from '../../../EditSession/useEditWriteGuard';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../../utils/trackTpoGenEvent';

export interface SaveAsOriginalResponse {
    status: string;
    plan_id: string;
    error_message?: string;
    message?: string;
}

const saveAsOriginal = async (optimizedPlanId: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.saveAsOriginal(optimizedPlanId), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (response?.data?.status !== 'success') {
        throw new Error(response?.data?.error_message || response?.data?.message || 'Failed to save as original');
    }

    return response.data as SaveAsOriginalResponse;
};

const useSaveAsOriginalMutation = ({
    onSuccess,
}: {
    onSuccess?: (data: SaveAsOriginalResponse) => void;
} = {}) => {
    const queryClient = useQueryClient();
    const assertCanWrite = useEditWriteGuard();

    const { mutate, isPending } = useMutation({
        mutationFn: (optimizedPlanId: string) => {
            assertCanWrite();
            return saveAsOriginal(optimizedPlanId);
        },
        onSuccess: (data, optimizedPlanId) => {
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'planning-groups'] });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            sendNotification('success', 'Optimized plan saved as original successfully');
            trackTpoGenEvent(TPOGEN_OPTIMIZED_PROMOTED_TO_ORIGINAL, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
                plan_id: data.plan_id,
                optimized_run_id: optimizedPlanId,
            });
            onSuccess?.(data);
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save as original';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useSaveAsOriginalMutation;
